<script setup lang="ts">
const { t } = useI18n();
const localePath = useLocalePath();

useHead({
  titleTemplate: () => t("seo.layouts./viber-transactional-messages.title"),
  meta: [
    { name: "description", content: () => t("seo.layouts./viber-transactional-messages.description") },
    { property: "og:title", content: () => t("seo.layouts./viber-transactional-messages.title") },
    { property: "og:description", content: () => t("seo.layouts./viber-transactional-messages.description") },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" }
  ]
});

const faqItems = ref([
  {
    question: t('intentPages.viberTransactionalMessages.faq.q1'),
    answer: t('intentPages.viberTransactionalMessages.faq.a1'),
    isOpen: false
  },
  {
    question: t('intentPages.viberTransactionalMessages.faq.q3'),
    answer: t('intentPages.viberTransactionalMessages.faq.a3'),
    isOpen: false
  },
  {
    question: t('intentPages.viberTransactionalMessages.faq.q2'),
    answer: t('intentPages.viberTransactionalMessages.faq.a2'),
    isOpen: false
  },
  {
    question: t('intentPages.viberTransactionalMessages.faq.q4'),
    answer: t('intentPages.viberTransactionalMessages.faq.a4'),
    isOpen: false
  },
  {
    question: t('intentPages.viberTransactionalMessages.faq.q5'),
    answer: t('intentPages.viberTransactionalMessages.faq.a5'),
    isOpen: false
  },
  {
    question: t('intentPages.viberTransactionalMessages.faq.q6'),
    answer: t('intentPages.viberTransactionalMessages.faq.a6'),
    isOpen: false
  }
]);

// Calculator State
const smsPrice = ref(0.04);
const viberPrice = ref(0.012);
const messagesCount = ref(100000);

const smsCost = computed(() => smsPrice.value * messagesCount.value);
const viberCost = computed(() => viberPrice.value * messagesCount.value);
const totalSavings = computed(() => smsCost.value - viberCost.value);

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

onMounted(() => {
  document.querySelector("body")?.scrollIntoView();
});
</script>

<template>
  <div class="provider-intent-page">
    
    <!-- Hero Banner -->
    <div class="main-banner bg-gradient-to-br from-blue-50 to-blue-100 pt-[120px] pb-20 px-4">
      <div class="container mx-auto max-w-5xl text-center">
        <h1 class="text-4xl md:text-6xl font-extrabold text-[#3070b6] mb-6 leading-tight">
          {{ $t('intentPages.viberTransactionalMessages.heroTitle') }}
        </h1>
        <p class="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto mb-6 leading-relaxed">
          {{ $t('intentPages.viberTransactionalMessages.heroSubtitle') }}
        </p>
        <p class="text-lg font-bold text-gray-800 bg-white inline-block px-4 py-2 rounded-lg shadow-sm mb-10 border border-gray-200">
          ⚡ {{ $t('intentPages.viberTransactionalMessages.heroStats') }}
        </p>
        <div class="flex justify-center flex-wrap gap-4">
          <a href="https://help.smsbat.com/api/smsbat/" target="_blank" class="nav-link button filled px-8 py-4 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition text-white bg-[#3070b6] hover:bg-blue-700">
            {{ $t('intentPages.viberTransactionalMessages.cta.buttonPrimary') }}
          </a>
          <NuxtLink :to="localePath('/contacts')" class="nav-link button outlined px-8 py-4 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition border-2 border-[#3070b6] text-[#3070b6] bg-transparent hover:bg-blue-50">
            {{ $t('intentPages.viberTransactionalMessages.cta.buttonSecondary') }}
          </NuxtLink>
        </div>
      </div>
    </div>

    <!-- What Is It -->
    <div class="page-section py-20 bg-white">
      <div class="container mx-auto max-w-4xl px-4">
        <h2 class="text-3xl font-bold text-gray-900 mb-6 text-center">{{ $t('intentPages.viberTransactionalMessages.whatIsTitle') }}</h2>
        <p class="text-xl text-gray-700 mb-8 text-center">{{ $t('intentPages.viberTransactionalMessages.whatIsSubtitle') }}</p>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto mb-10">
          <div v-for="(item, index) in $tm('intentPages.viberTransactionalMessages.whatIsList')" :key="index" class="flex items-center text-lg text-gray-800 bg-blue-50 px-4 py-3 rounded-lg border border-blue-100">
            <span class="text-[#3070b6] mr-3 font-bold">•</span>
            {{ $rt(item) }}
          </div>
        </div>

        <div class="bg-gray-50 border-l-4 border-[#3070b6] p-6 rounded-r-lg shadow-sm text-lg text-gray-800">
          {{ $t('intentPages.viberTransactionalMessages.whatIsFooter') }}
        </div>
      </div>
    </div>

    <!-- Viber Vs SMS Comparison -->
    <div class="page-section py-20 bg-gradient-to-b from-white to-gray-50 border-t border-gray-100">
      <div class="container mx-auto max-w-5xl px-4">
        <h2 class="text-3xl font-bold text-gray-900 mb-12 text-center">{{ $t('intentPages.viberTransactionalMessages.comparisonTitle') }}</h2>
        
        <div class="overflow-x-auto bg-white rounded-2xl shadow-sm border border-gray-200">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th class="p-5 font-bold text-gray-700 w-1/3">Feature</th>
                <th class="p-5 font-bold text-gray-700 w-1/3 text-center border-l border-gray-200">{{ $t('intentPages.viberTransactionalMessages.comparisonSms') }}</th>
                <th class="p-5 font-extrabold text-[#3070b6] w-1/3 text-center border-l border-gray-200 bg-blue-50/50">{{ $t('intentPages.viberTransactionalMessages.comparisonViber') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(feature, index) in $tm('intentPages.viberTransactionalMessages.comparisonFeatures')" :key="index" class="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td class="p-4 font-medium text-gray-800">{{ $rt(feature) }}</td>
                <td class="p-4 text-center text-gray-600 border-l border-gray-200 bg-white">{{ $rt(($tm('intentPages.viberTransactionalMessages.comparisonValues.sms') as Record<string, any>)[index]) }}</td>
                <td class="p-4 text-center font-bold text-[#3070b6] border-l border-blue-100 bg-blue-50/20">{{ $rt(($tm('intentPages.viberTransactionalMessages.comparisonValues.viber') as Record<string, any>)[index]) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <p class="text-center mt-8 text-lg text-gray-600 max-w-3xl mx-auto">
          {{ $t('intentPages.viberTransactionalMessages.comparisonFooter') }}
        </p>
      </div>
    </div>

    <!-- Smart Transactional Messaging Engine (Tech) -->
    <div class="page-section py-20 bg-white border-t border-gray-100">
      <div class="container mx-auto max-w-5xl px-4">
        <div class="flex flex-col lg:flex-row items-center gap-12">
          <div class="lg:w-1/2">
            <h2 class="text-3xl font-bold text-gray-900 mb-6">{{ $t('intentPages.viberTransactionalMessages.techTitle') }}</h2>
            <p class="text-xl text-gray-700 mb-6">{{ $t('intentPages.viberTransactionalMessages.techSubtitle') }}</p>
            
            <ul class="space-y-3 mb-8">
              <li v-for="(item, index) in $tm('intentPages.viberTransactionalMessages.techAnalyzes')" :key="index" class="flex items-start">
                <i class="pi pi-check-circle text-[#3070b6] mt-1 mr-3"></i>
                <span class="text-gray-800 text-lg">{{ $rt(item) }}</span>
              </li>
            </ul>

            <p class="text-xl font-bold text-gray-900 mb-6">{{ $t('intentPages.viberTransactionalMessages.techAllowsText') }}</p>
            
            <ul class="space-y-3 mb-8 p-6 bg-blue-50 rounded-xl border border-blue-100">
              <li v-for="(item, index) in $tm('intentPages.viberTransactionalMessages.techAllows')" :key="index" class="flex items-start">
                <i class="pi pi-star-fill text-[#3070b6] mt-1 mr-3"></i>
                <span class="text-gray-800 text-lg font-medium">{{ $rt(item) }}</span>
              </li>
            </ul>

            <div class="inline-block bg-[#3070b6] text-white px-6 py-3 rounded-lg font-bold shadow-md">
              ⏱️ {{ $t('intentPages.viberTransactionalMessages.techFooter') }}
            </div>
          </div>
          
          <div class="lg:w-1/2 w-full flex justify-center">
            <!-- Simulated Tech Graphic -->
            <div class="w-full max-w-md bg-gray-900 rounded-2xl shadow-2xl p-6 border border-gray-700 relative overflow-hidden">
              <div class="absolute top-0 right-0 p-4 opacity-10">
                <i class="pi pi-bolt text-9xl text-white"></i>
              </div>
              <div class="flex items-center justify-between mb-8 border-b border-gray-700 pb-4">
                <span class="text-green-400 font-mono">LLM Profile Active</span>
                <span class="bg-blue-600 text-xs px-2 py-1 rounded text-white font-bold">130ms SLA</span>
              </div>
              <div class="space-y-4 font-mono text-sm">
                <div class="flex justify-between text-gray-400"><span>> analyzing_request...</span> <span class="text-green-400">OK</span></div>
                <div class="flex justify-between text-gray-400"><span>> matching_pattern...</span> <span class="text-green-400">OTP_FOUND</span></div>
                <div class="flex justify-between text-gray-400"><span>> verifying_template...</span> <span class="text-green-400">APPROVED</span></div>
                <div class="mt-4 p-3 bg-gray-800 rounded border border-gray-700 text-white">
                  { "status": "dispatching", "channel": "viber_transactional", "eta": "immediate" }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Savings Calculator -->
    <div class="page-section py-20 bg-blue-900 text-white border-y border-blue-800">
      <div class="container mx-auto max-w-5xl px-4 text-center">
        <h2 class="text-3xl md:text-4xl font-bold mb-6">{{ $t('intentPages.viberTransactionalMessages.savingsTitle') }}</h2>
        <p class="text-xl text-blue-200 mb-10 max-w-3xl mx-auto">{{ $t('intentPages.viberTransactionalMessages.savingsSubtitle') }}</p>
        
        <div class="bg-white rounded-2xl p-8 max-w-4xl mx-auto shadow-2xl text-gray-900 text-left">
          <h3 class="text-2xl font-bold mb-8 text-center text-[#3070b6]">
            {{ $t('intentPages.viberTransactionalMessages.calculator.title') }}
          </h3>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
            <!-- Controls -->
            <div class="space-y-6">
              <div>
                <label for="messagesCount" class="block text-sm font-bold text-gray-700 mb-2">
                  {{ $t('intentPages.viberTransactionalMessages.calculator.messages') }}
                </label>
                <div class="flex items-center gap-4">
                  <input id="messagesCount" type="range" v-model.number="messagesCount" min="1000" max="1000000" step="1000" class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#3070b6]">
                  <span class="font-mono font-bold w-24 text-right bg-gray-100 p-2 rounded">{{ new Intl.NumberFormat('en-US').format(messagesCount) }}</span>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label for="smsPrice" class="block text-sm font-bold text-gray-700 mb-2">
                    {{ $t('intentPages.viberTransactionalMessages.calculator.smsPrice') }}
                  </label>
                  <div class="relative">
                    <span class="absolute left-3 top-2.5 text-gray-500">$</span>
                    <input id="smsPrice" type="number" v-model.number="smsPrice" step="0.001" class="w-full border border-gray-300 rounded-lg py-2 pl-8 pr-3 focus:outline-none focus:border-[#3070b6] focus:ring-1 focus:ring-[#3070b6]">
                  </div>
                </div>
                <div>
                  <label for="viberPrice" class="block text-sm font-bold text-gray-700 mb-2">
                    {{ $t('intentPages.viberTransactionalMessages.calculator.viberPrice') }}
                  </label>
                  <div class="relative">
                    <span class="absolute left-3 top-2.5 text-gray-500">$</span>
                    <input id="viberPrice" type="number" v-model.number="viberPrice" step="0.001" class="w-full border border-gray-300 rounded-lg py-2 pl-8 pr-3 focus:outline-none focus:border-[#3070b6] focus:ring-1 focus:ring-[#3070b6]">
                  </div>
                </div>
              </div>
            </div>

            <!-- Results -->
            <div class="bg-gray-50 rounded-xl p-6 border border-gray-200 flex flex-col justify-center">
              <div class="flex justify-between items-center mb-4 pb-4 border-b border-gray-200">
                <span class="text-gray-600 font-medium">{{ $t('intentPages.viberTransactionalMessages.calculator.smsCost') }}</span>
                <span class="text-xl font-mono text-gray-400 line-through">{{ formatCurrency(smsCost) }}</span>
              </div>
              <div class="flex justify-between items-center mb-6">
                <span class="text-gray-800 font-bold">{{ $t('intentPages.viberTransactionalMessages.calculator.viberCost') }}</span>
                <span class="text-2xl font-mono text-gray-900">{{ formatCurrency(viberCost) }}</span>
              </div>
              
              <div class="bg-green-100 border border-green-200 p-4 rounded-lg text-center">
                <span class="block text-green-800 font-bold mb-1">{{ $t('intentPages.viberTransactionalMessages.calculator.totalSavings') }}</span>
                <span class="text-3xl font-black text-green-600 tracking-tight">{{ formatCurrency(totalSavings) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section with Default Rates Table -->
    <div class="page-section py-20 bg-gray-50 border-y border-gray-200">
      <div class="container mx-auto max-w-5xl px-4 text-center">
        <h2 class="text-3xl font-bold text-gray-900 mb-4">{{ $t('intentPages.viberTransactionalMessages.pricing.title') }}</h2>
        <p class="text-xl text-gray-600 mb-10">{{ $t('intentPages.viberTransactionalMessages.pricing.subtitle') }}</p>
        
        <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 overflow-x-auto">
          <BMDefaultRatesTable />
        </div>
      </div>
    </div>

    <!-- Calls to Action (Primary Lead Gen) -->
    <div class="page-section py-24 bg-white text-center">
      <div class="container mx-auto max-w-4xl px-4">
        <h2 class="text-4xl font-extrabold text-[#3070b6] mb-6">
          {{ $t('intentPages.viberTransactionalMessages.cta.title') }}
        </h2>
        <p class="text-xl text-gray-700 max-w-2xl mx-auto mb-10">
          {{ $t('intentPages.viberTransactionalMessages.cta.subtitle') }}
        </p>
        
        <div class="flex flex-wrap justify-center gap-6 mb-12">
          <div v-for="(benefit, index) in $tm('intentPages.viberTransactionalMessages.cta.benefits')" :key="index" class="flex items-center text-gray-800 font-bold bg-blue-50 px-4 py-2 rounded-full border border-blue-100">
            <i class="pi pi-check text-[#3070b6] mr-2"></i> {{ $rt(benefit) }}
          </div>
        </div>

        <div class="flex justify-center flex-wrap gap-4">
          <a href="https://cabinet.smsbat.com/" class="nav-link button filled px-10 py-5 text-xl font-bold rounded-xl shadow-lg hover:shadow-xl transition text-white bg-[#3070b6] hover:bg-blue-700">
            {{ $t('intentPages.viberTransactionalMessages.cta.buttonPrimary') }}
          </a>
          <NuxtLink :to="localePath('/contacts')" class="nav-link button outlined px-10 py-5 text-xl font-bold rounded-xl shadow-lg hover:shadow-xl transition border-2 border-gray-300 text-gray-700 bg-white hover:bg-gray-50">
            {{ $t('intentPages.viberTransactionalMessages.cta.buttonSecondary') }}
          </NuxtLink>
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <div class="page-section py-20 bg-gray-50 border-t border-gray-200">
      <div class="container mx-auto max-w-4xl px-4">
        <PopularQuestions :title="$t('intentPages.viberTransactionalMessages.faqTitle')" :questions="faqItems" />
      </div>
    </div>

    <!-- Deep SEO Block -->
    <div class="page-section py-20 bg-white border-t border-gray-100 text-gray-600">
      <div class="container mx-auto max-w-4xl px-4">
        <h2 class="text-2xl font-bold text-gray-900 mb-6">{{ $t('intentPages.viberTransactionalMessages.seoBlock.title') }}</h2>
        <p class="mb-6 leading-relaxed">{{ $t('intentPages.viberTransactionalMessages.seoBlock.content') }}</p>
        
        <ul class="list-disc pl-6 space-y-2 mb-6">
          <li v-for="(caseItem, index) in $tm('intentPages.viberTransactionalMessages.seoBlock.useCases')" :key="index">
            {{ $rt(caseItem) }}
          </li>
        </ul>
        
        <p class="leading-relaxed font-medium">{{ $t('intentPages.viberTransactionalMessages.seoBlock.footer') }}</p>
      </div>
    </div>
    
  </div>
</template>

<style scoped lang="scss">
.provider-intent-page {
  .button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
